import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../contexts/LanguageContext";
import "./InvestedMoneyUsed.css";

const InvestedMoneyUsed = () => {
  const { translations: tRaw } = useLanguage();
  // Safe object with fallbacks
  const t = {
    ...tRaw,
    investment: {
      ...(tRaw?.investment || {})
    }
  };

  const [hovered, setHovered] = useState(null);

  // Build translated data (recomputes on language change)
  const allocationData = useMemo(() => ([
    {
      key: "technicalInfrastructure",
      category: t.investment?.categories?.technicalInfrastructure || "Technical Infrastructure",
      percentage: 20,
      // description: t.investment?.descriptions?.technicalInfrastructure || "Development and improvement of the technical backbone",
      color: "#FF5252",
    },
    {
      key: "marketingCampaigns",
      category: t.investment?.categories?.marketingCampaigns || "Marketing",
      percentage: 30,
      // description: t.investment?.descriptions?.marketingCampaigns || "Digital marketing and launch campaigns",
      color: "#33A852",
    },
    {
      key: "strategicPartnerships",
      category: t.investment?.categories?.strategicPartnerships || "Strategic Partnerships",
      percentage: 10,
      // description: t.investment?.descriptions?.strategicPartnerships || "Deals with payment providers and major stores",
      color: "#8BC34A",
    },
    {
      key: "marketExpansion",
      category: t.investment?.categories?.marketExpansion || "Market Expansion",
      percentage: 15,
      // description: t.investment?.descriptions?.marketExpansion || "Entering new Gulf markets",
      color: "#2196F3",
    },
    {
      key: "featureDevelopment",
      category: t.investment?.categories?.featureDevelopment || "Development and work team ",
      percentage: 25,
      // description: t.investment?.descriptions?.featureDevelopment || "AI tools and user experience improvements",
      color: "#9C27B0",
    },
  ]), [t]);

  /** ---- Donut geometry ---- */
  const size   = 460;
  const cx     = size / 2;
  const cy     = size / 2;
  const outerR = 172;
  const thick  = 74;
  const innerR = outerR - thick;

  // no gap normally; tiny gap on hover
  const baseGapDeg  = 0;
  const hoverGapDeg = 2.5;
  const pop         = 14;

  const toRad = (deg) => (Math.PI / 180) * deg;
  const polar = (r, deg) => ({ x: cx + r * Math.cos(toRad(deg)), y: cy + r * Math.sin(toRad(deg)) });

  const ringPath = (rOuter, rInner, start, end) => {
    const large = end - start > 180 ? 1 : 0;
    const p1 = polar(rOuter, start);
    const p2 = polar(rOuter, end);
    const p3 = polar(rInner, end);
    const p4 = polar(rInner, start);
    return [
      `M ${p1.x} ${p1.y}`,
      `A ${rOuter} ${rOuter} 0 ${large} 1 ${p2.x} ${p2.y}`,
      `L ${p3.x} ${p3.y}`,
      `A ${rInner} ${rInner} 0 ${large} 0 ${p4.x} ${p4.y}`,
      `Z`,
    ].join(" ");
  };

  // Use 0 gap normally; introduce a tiny gap only while hovering
  const arcs = useMemo(() => {
    const gapDeg = hovered == null ? baseGapDeg : hoverGapDeg;
    let a = -90;
    return allocationData.map((d, i) => {
      const sweep = (d.percentage / 100) * 360;
      const start = a + gapDeg / 2;
      const end   = a + sweep - gapDeg / 2;
      const mid   = (start + end) / 2;
      a += sweep;
      return { ...d, start, end, mid, i };
    });
  }, [allocationData, hovered]);

  const active = hovered != null ? arcs[hovered] : null;

  return (
    <section id="invested-money-used" className="invested-money-section">
      <div className="container">
        <motion.h2
          className="section-investment-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {t.investment?.capitalAllocationTitle || "Capital Allocation"}
        </motion.h2>

        <motion.p
          className="section-investment-subtitle"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {t.investment?.capitalAllocationSubtitle || "How the invested capital will be utilized"}
        </motion.p>

        <div className="allocation-container">
          <div className="chart-container">
            <svg className="donut-svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
              <defs>
                <filter id="sliceShadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="rgba(2,24,46,.22)" />
                  <feDropShadow dx="0" dy="2" stdDeviation="6" floodColor="rgba(16,160,255,.25)" />
                </filter>
                <radialGradient id="outerSheen" cx="50%" cy="50%" r="70%">
                  <stop offset="72%" stopColor="rgba(255,255,255,.22)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </radialGradient>
                <filter id="centerShadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="10" stdDeviation="14" floodColor="rgba(2,24,46,.16)" />
                </filter>
              </defs>

              <circle cx={cx} cy={cy} r={outerR + 18} fill="url(#outerSheen)" />

              {arcs.map((seg, i) => {
                const isActive = i === hovered;
                const dx = isActive ? pop * Math.cos(toRad(seg.mid)) : 0;
                const dy = isActive ? pop * Math.sin(toRad(seg.mid)) : 0;
                return (
                  <path
                    key={seg.key}
                    d={ringPath(outerR, innerR, seg.start, seg.end)}
                    fill={seg.color}
                    transform={`translate(${dx}, ${dy})`}
                    filter={isActive ? "url(#sliceShadow)" : "none"}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                    style={{ cursor: "pointer", transition: "transform .22s ease, filter .22s ease" }}
                  />
                );
              })}

              {/* Slightly bigger center to ensure a tight, clean inner join */}
              <circle cx={cx} cy={cy} r={innerR - 3} fill="#fff" filter="url(#centerShadow)" />

              {active ? (
                <>
                  <text x={cx} y={cy - 6} textAnchor="middle" className="center-percent">
                    {active.percentage}%
                  </text>
                  <text x={cx} y={cy + 24} textAnchor="middle" className="center-label">
                    {active.category}
                  </text>
                </>
              ) : (
                <text x={cx} y={cy + 8} textAnchor="middle" className="center-percent">
                  {t.investment?.centerTotal || "100%"}
                </text>
              )}
            </svg>
          </div>

          {/* Right-side “dice” cards */}
          <div className="allocation-details dice-layout">
            <div className="dice-row">
              {allocationData.slice(0, 2).map((item, index) => (
                <motion.div
                  key={item.key}
                  className="allocation-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="color-indicator" style={{ backgroundColor: item.color }} />
                  <div className="card-content">
                    <h3>{item.category}</h3>
                    <p className="percentage">{item.percentage}%</p>
                    <p className="description">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="dice-row middle-row">
              <motion.div
                className="allocation-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="color-indicator" style={{ backgroundColor: allocationData[2].color }} />
                <div className="card-content">
                  <h3>{allocationData[2].category}</h3>
                  <p className="percentage">{allocationData[2].percentage}%</p>
                  <p className="description">{allocationData[2].description}</p>
                </div>
              </motion.div>
            </div>

            <div className="dice-row">
              {allocationData.slice(3, 5).map((item, index) => (
                <motion.div
                  key={item.key}
                  className="allocation-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 * (index + 4) }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="color-indicator" style={{ backgroundColor: item.color }} />
                  <div className="card-content">
                    <h3>{item.category}</h3>
                    <p className="percentage">{item.percentage}%</p>
                    <p className="description">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InvestedMoneyUsed;
